<template>
  <div class="transaction-code-management">
    <h1>Manajemen Kode Transaksi</h1>
    <p>Kelola sandi dan jenis transaksi yang digunakan di seluruh sistem.</p>

    <!-- Tombol Tambah -->
    <button @click="openForm()" class="btn btn-primary" style="margin-bottom: 1rem;">
      Tambah Kode Baru
    </button>

    <!-- Modal Form Tambah/Edit -->
    <div v-if="isFormVisible" class="modal-overlay" @click.self="closeForm">
      <div class="modal-content">
        <h2>{{ form.id ? 'Edit' : 'Tambah' }} Kode Transaksi</h2>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="form-code">Sandi (Kode)</label>
            <input v-model="form.code" id="form-code" required placeholder="Contoh: 101" />
          </div>
          <div class="form-group">
            <label for="form-name">Nama Transaksi</label>
            <input v-model="form.name" id="form-name" required placeholder="Contoh: Transfer Keluar" />
          </div>
          <div class="form-group">
            <label for="form-description">Deskripsi</label>
            <input v-model="form.description" id="form-description" placeholder="Opsional" />
          </div>
          
          <!-- Input Tipe Internal -->
          <div class="form-group">
            <label for="form-internal-type">Tipe Internal (Untuk Sistem)</label>
            <input
              v-model="form.internal_type"
              id="form-internal-type"
              placeholder="Contoh: CASH_DEPOSIT"
              :disabled="form.id"
            />
            <small v-if="!form.id" class="hint">Kosongkan jika bukan kode sistem. Tidak bisa diubah setelah dibuat.</small>
          </div>

          <div class="checkbox-group">
            <label>
              <input type="checkbox" v-model="form.is_debit" />
              Ini adalah Transaksi Debet (Mengurangi Saldo)
            </label>
          </div>
          <div class="checkbox-group">
            <label>
              <input type="checkbox" v-model="form.is_credit" />
              Ini adalah Transaksi Kredit (Menambah Saldo)
            </label>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeForm">Batal</button>
            <button type="submit" class="btn btn-primary">
              {{ form.id ? 'Simpan Perubahan' : 'Simpan' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Tabel Daftar Kode Transaksi -->
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>Sandi</th>
            <th>Nama</th>
            <th>Tipe Internal</th>
            <th>Tipe</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="code in transactionCodes" :key="code.id">
            <td><strong>{{ code.code }}</strong></td>
            <td>{{ code.name }}</td>
            <td>{{ code.internal_type || '—' }}</td>
            <td>
              <span v-if="code.is_debit">Debet</span>
              <span v-if="code.is_credit">{{ code.is_debit ? ' / ' : '' }}Kredit</span>
              <span v-if="!code.is_debit && !code.is_credit">—</span>
            </td>
            <td class="actions">
              <button @click="openForm(code)" class="btn btn-sm">Edit</button>
              <button @click="handleDelete(code.id)" class="btn btn-sm btn-danger">Hapus</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'; 
import api from '@/services/api';
import { useToast } from 'vue-toastification';

const toast = useToast();
const transactionCodes = ref([]);
const isFormVisible = ref(false);

const form = reactive({
  id: null,
  code: '',
  name: '',
  description: '',
  is_debit: false,
  is_credit: false,
  internal_type: null
});

const openForm = (code = null) => {
  if (code) { // Mode Edit
    Object.assign(form, {
      id: code.id,
      code: code.code,
      name: code.name,
      description: code.description || '',
      is_debit: code.is_debit || false,
      is_credit: code.is_credit || false,
      internal_type: code.internal_type || null
    });
  } else { // Mode Tambah
    Object.assign(form, {
      id: null, code: '', name: '', description: '',
      is_debit: false, is_credit: false, internal_type: null
    });
  }
  isFormVisible.value = true;
};

const closeForm = () => {
  isFormVisible.value = false;
};

const fetchTransactionCodes = async () => {
  try {
    const response = await api.getTransactionCodes();
    transactionCodes.value = response.data;
  } catch (error) {
    toast.error('Gagal memuat daftar kode transaksi.');
  }
};

const handleSubmit = async () => {
  try {
    if (form.id) { // Update
      // Kirim seluruh form. Backend akan mengabaikan 'internal_type' jika tidak ada di skema Update.
      await api.updateTransactionCode(form.id, form);
      toast.success('Kode transaksi berhasil diperbarui.');
    } else { // Create
      // Kirim seluruh form. Backend akan menerima 'internal_type' jika ada di skema Create.
      await api.createTransactionCode(form);
      toast.success('Kode transaksi baru berhasil ditambahkan.');
    }
    closeForm();
    await fetchTransactionCodes();
  } catch (error) {
    const msg = error.response?.data?.detail || 'Operasi gagal. Pastikan kode unik.';
    toast.error(msg);
  }
};

const handleDelete = async (id) => {
  if (confirm('Yakin ingin menghapus kode transaksi ini?')) {
    try {
      await api.deleteTransactionCode(id);
      toast.success('Kode transaksi berhasil dihapus.');
      await fetchTransactionCodes();
    } catch (error) {
      toast.error('Gagal menghapus kode transaksi.');
    }
  }
};

onMounted(fetchTransactionCodes);

// Hook ini akan dijalankan tepat sebelum Anda meninggalkan halaman ini
onUnmounted(() => {
  isFormVisible.value = false; // Pastikan modal selalu tertutup
});
</script>
<style scoped>
.transaction-code-management {
  padding: 1.5rem;
}

h1 {
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.table-container {
  overflow-x: auto;
  margin-top: 1rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  border-radius: 8px;
}

th, td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

tr:last-child td {
  border-bottom: none;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}
.btn-primary:hover {
  background-color: #0069d9;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}
.btn-danger:hover {
  background-color: #c82333;
}

.btn-sm {
  padding: 0.3rem 0.6rem;
  font-size: 0.85rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.checkbox-group {
  margin-bottom: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.form-actions button {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
}

.form-actions button:first-child {
  background: #6c757d;
  color: white;
}
.form-actions button:first-child:hover {
  background: #5a6268;
}
</style>